import { useState, useEffect, useMemo, useCallback } from 'react';
import { Briefcase, FileText, BarChart2, MapPin, Search, MessageSquare, DollarSign, Tag, ChevronLeft, ChevronRight, X, History, Bookmark, BookmarkCheck } from 'lucide-react';
import { apiCall, getErrorMessage } from './utils/api';

// --- SHARED COMPONENTS (Could be moved to their own file) ---
const Input = ({ label, ...props }) => (
    <div className="space-y-2 w-full">
        {label && <label className="text-sm font-medium text-[#3C0F6C]/80">{label}</label>}
        <input {...props} className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow placeholder:text-[#3C0F6C]/50" />
    </div>
);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper to convert resume data object to a string, used by multiple components
const formatResumeDataAsText = (data) => {
    if (!data || !data.personalInfo) return "";
    let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
    if (data.goal) text += `## Goal\n${data.goal}\n\n`;
    text += "## Work Experience\n";
    (data.experience || []).forEach(exp => {
        text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
    });
    text += "\n## Education\n";
    (data.education || []).forEach(edu => {
        text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
    });
    text += "\n## Projects\n";
    (data.projects || []).forEach(proj => {
        text += `- ${proj.title} (${proj.period})\n  ${proj.description}\n`;
    });
    text += "\n## Skills\n";
    {
        const skills = Array.isArray(data.skills)
            ? data.skills.filter(Boolean).map(String)
            : (typeof data.skills === 'string'
                ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
                : []);
        text += skills.join(', ');
    }
    text += "\n\n## Achievements\n";
    (data.achievements || []).forEach(ach => {
        text += `- ${ach.description}\n`;
    });
    text += "\n## Awards\n";
    (data.awards || []).forEach(award => {
        text += `- ${award.description}\n`;
    });
    text += "\n## Certificates\n";
    (data.certificates || []).forEach(cert => {
        text += `- ${cert.description}\n`;
    });
    text += "\n## Hobbies\n";
    (data.hobbies || []).forEach(hobby => {
        text += `- ${hobby.description}\n`;
    });
    return text;
};


// --- NEW CACHING HOOK ---
const useJobCache = (cacheKey) => {
    const [cachedData, setCachedData] = useState(() => {
        try {
            const item = window.sessionStorage.getItem(cacheKey);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.error("Error reading from sessionStorage", error);
            return null;
        }
    });

    const setData = useCallback((newData) => {
        try {
            const valueToStore = JSON.stringify(newData);
            window.sessionStorage.setItem(cacheKey, valueToStore);
            setCachedData(newData);
        } catch (error) {
            console.error("Error writing to sessionStorage", error);
        }
    }, [cacheKey]);

    useEffect(() => {
        try {
            const item = window.sessionStorage.getItem(cacheKey);
            setCachedData(item ? JSON.parse(item) : null);
        } catch (error) {
            console.error("Error reading from sessionStorage", error);
            setCachedData(null);
        }
    }, [cacheKey]);

    return [cachedData, setData];
};


const JobMatching = ({ resumeData, onGenerateCoverLetter, onGenerateInterviewQuestions, user }) => {

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState({ what: resumeData?.experience?.[0]?.title || '', where: '' });
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState({ what: resumeData?.experience?.[0]?.title || '', where: '' });

    const [selectedJob, setSelectedJob] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('gb');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchHistory, setSearchHistory] = useState([]);
    const [savedJobs, setSavedJobs] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('savedJobs')) || [];
        } catch { return []; }
    });

    useEffect(() => {
        const history = JSON.parse(localStorage.getItem('jobSearchHistory')) || [];
        setSearchHistory(history);
        try {
            setSavedJobs(JSON.parse(localStorage.getItem('savedJobs')) || []);
        } catch { }
        const onStorage = (e) => {
            if (e.key === 'savedJobs') {
                try { setSavedJobs(JSON.parse(e.newValue) || []); } catch { setSavedJobs([]); }
            }
        };
        const onCustom = () => {
            try { setSavedJobs(JSON.parse(localStorage.getItem('savedJobs')) || []); } catch { setSavedJobs([]); }
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener('saved-jobs-updated', onCustom);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('saved-jobs-updated', onCustom);
        };
    }, []);

    const cacheKey = useMemo(() => {

        if (!debouncedSearchQuery.what) return `jobs:match:${selectedCountry}:${currentPage}`;

        const what = debouncedSearchQuery.what.trim().toLowerCase();
        const where = debouncedSearchQuery.where.trim().toLowerCase();

        if (what || where) {
            return `jobs:search:${selectedCountry}:${what}:${where}:${currentPage}`;
        }
        return `jobs:match:${selectedCountry}:${currentPage}`;
    }, [debouncedSearchQuery, selectedCountry, currentPage]);

    const [jobData, setJobData] = useJobCache(cacheKey);

    const jobs = jobData?.results || [];
    const totalJobs = jobData?.count || 0;

    const totalPages = Math.min(10, Math.ceil(totalJobs / 10));

    const fetchJobs = useCallback(async (url, body) => {
        setIsLoading(true);
        setError(null);
        try {
            // Extract endpoint from full URL (apiCall expects endpoint like "/jobs/search")
            const endpoint = url.replace(API_BASE_URL, '');

            // Use apiCall utility to automatically include auth token
            const { data } = await apiCall(endpoint, {
                method: 'POST',
                body: body,
            });

            // Normalize to safe shape
            const normalized = {
                results: Array.isArray(data?.results) ? data.results : [],
                count: Number.isFinite(data?.count) ? data.count : (Array.isArray(data?.results) ? data.results.length : 0),
                error: data?.error || null,
            };

            setJobData(normalized);
            console.log("Fetched jobs:", normalized);

            if (normalized.results.length > 0) {
                if (currentPage === 1) {
                    setSelectedJob(normalized.results[0]);
                }
            } else {
                setSelectedJob(null);
            }
        } catch (err) {
            console.error("Job fetching error:", err);
            setError(getErrorMessage(err));
            setJobData(null);
            setSelectedJob(null);
        } finally {
            setIsLoading(false);
        }
    }, [setJobData, currentPage]);

    const handleSearch = useCallback((e) => {
        if (e) e.preventDefault();

        const newSearch = { what: searchQuery.what, where: searchQuery.where, country: selectedCountry };
        if (newSearch.what || newSearch.where) {
            const history = [newSearch, ...searchHistory.filter(s => s.what !== newSearch.what || s.where !== newSearch.where)].slice(0, 5);
            setSearchHistory(history);
            localStorage.setItem('jobSearchHistory', JSON.stringify(history));
        }
        // Ensure immediate search with the latest typed values (bypass debounce on submit)
        setDebouncedSearchQuery(newSearch);
        setCurrentPage(1);
    }, [searchQuery, selectedCountry, searchHistory]);

    const handleClearSearch = () => {
        setSearchQuery({ what: '', where: '' });
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleSelectJob = (job) => {
        setSelectedJob(job);
        console.log("Selected job:", job);
    };

    const isSaved = useCallback((job) => savedJobs.some(j => j.id === job.id), [savedJobs]);
    const persistSaved = useCallback((list) => {
        localStorage.setItem('savedJobs', JSON.stringify(list));
        window.dispatchEvent(new Event('saved-jobs-updated'));
        setSavedJobs(list);
    }, []);
    const toggleSave = useCallback((job) => {
        const minimal = {
            id: job.id,
            title: job.title,
            company: job.company?.display_name,
            location: job.location?.display_name,
            redirect_url: job.redirect_url,
            description: job.description,
            category: job.category?.label || '',
            saved_at: Date.now(),
        };
        if (isSaved(job)) {
            persistSaved(savedJobs.filter(j => j.id !== job.id));
        } else {
            persistSaved([minimal, ...savedJobs.filter(j => j.id !== job.id)]);
        }
    }, [savedJobs, isSaved, persistSaved]);

    const handleFetchMatchDetails = async (jobToDetail) => {
        if (jobToDetail.matchDetails || isDetailLoading) return;

        setIsDetailLoading(true);
        const formData = new FormData();
        formData.append('resume_text', formatResumeDataAsText(resumeData));
        formData.append('job_description', jobToDetail.description);
        try {
            const { data } = await apiCall('/api/jobs/match-details', {
                method: 'POST',
                body: formData,
            });

            const updatedJobs = jobs.map(j =>
                j.id === jobToDetail.id ? { ...j, matchDetails: data } : j
            );
            setJobData({ ...jobData, results: updatedJobs });
            setSelectedJob({ ...jobToDetail, matchDetails: data });

        } catch (err) {
            console.error("Error fetching match details:", err);
            const errorDetails = { error: err.message };
            const updatedJobs = jobs.map(j =>
                j.id === jobToDetail.id ? { ...j, matchDetails: errorDetails } : j
            );
            setJobData({ ...jobData, results: updatedJobs });
            setSelectedJob({ ...jobToDetail, matchDetails: errorDetails });
        } finally {
            setIsDetailLoading(false);
        }
    };

    useEffect(() => {
        // Don't fetch jobs until user is authenticated
        if (!user) {
            return;
        }

        const formData = new FormData();
        formData.append('country', selectedCountry);
        formData.append('page', currentPage);

        if (debouncedSearchQuery.what || debouncedSearchQuery.where) {
            formData.append('what', debouncedSearchQuery.what);
            formData.append('where', debouncedSearchQuery.where);
            fetchJobs(`${API_BASE_URL}/api/jobs/search`, formData);
        } else if (resumeData) {
            const resumeText = formatResumeDataAsText(resumeData);
            formData.append('resume_text', resumeText);
            fetchJobs(`${API_BASE_URL}/api/jobs/match`, formData);
        } else {
            fetchJobs(`${API_BASE_URL}/api/jobs/search`, formData);
        }
    }, [user, currentPage, debouncedSearchQuery, selectedCountry, resumeData, fetchJobs]);

    // Debounce user typing in search inputs to reduce network calls
    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 600); // 600ms debounce within requested 500–700ms range
        return () => clearTimeout(handle);
    }, [searchQuery]);

    // FILTERED COUNTRIES: Only India, USA, Canada, UK, Singapore
    const supportedCountries = [
        { code: 'in', name: 'India' },
        { code: 'us', name: 'United States' },
        { code: 'ca', name: 'Canada' },
        { code: 'gb', name: 'United Kingdom' },
        { code: 'sg', name: 'Singapore' },
    ];

    return (
        <div>
            <header className="mb-8">
                <h2 className="text-3xl font-bold text-black-500">AI-Powered Job Matching</h2>
                <p className="text-[#b473e6]/80 mt-1">Discover opportunities tailored to your unique profile and skills.</p>
            </header>

            <form onSubmit={handleSearch} className="mb-4 p-4 bg-[#fafafa] rounded-xl border border-[#eac7f0] shadow-sm grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div className="md:col-span-1">
                    <Input
                        label="Job Title / Keywords"
                        value={searchQuery.what}
                        onChange={(e) => setSearchQuery({ ...searchQuery, what: e.target.value })}
                        placeholder="e.g., Software Engineer"
                    />
                </div>
                <div className="md:col-span-1">
                    <Input
                        label="Location"
                        value={searchQuery.where}
                        onChange={(e) => setSearchQuery({ ...searchQuery, where: e.target.value })}
                        placeholder="e.g., London"
                    />
                </div>
                <div className="md:col-span-1">
                    <label className="text-sm font-medium text-[#3C0F6C]/80 block mb-2">Country</label>
                    <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="w-full px-4 py-3 bg-[#fafafa] border border-[#d5e7f3] text-[#3C0F6C] rounded-xl focus:ring-2 focus:ring-[#3C0F6C] focus:border-[#3C0F6C] transition-shadow"
                    >
                        {supportedCountries.map(country => (
                            <option key={country.code} value={country.code}>{country.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <button type="submit" className="w-full bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors flex items-center justify-center" aria-label="Search">
                        <Search size={20} />
                    </button>
                    <button type="button" onClick={handleClearSearch} className="w-full bg-[#d5e7f3] text-[#3C0F6C] font-semibold py-3 rounded-xl hover:bg-[#fafafa] transition-colors flex items-center justify-center" aria-label="Clear Search">
                        <X size={20} />
                    </button>
                </div>
            </form>

            {searchHistory.length > 0 && (
                <div className="mb-8 p-4 bg-[#fafafa] rounded-xl border border-[#d5e7f3] shadow-sm">
                    <h3 className="text-sm font-semibold text-[#3C0F6C]/80 mb-2 flex items-center gap-2"><History size={16} /> Recent Searches</h3>
                    <div className="flex flex-wrap gap-2">
                        {searchHistory.map((search, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setSearchQuery({ what: search.what, where: search.where });
                                    setSelectedCountry(search.country);
                                    setCurrentPage(1);
                                }}
                                className="px-3 py-1.5 text-xs bg-[#d5e7f3] text-[#3C0F6C] border border-[#d5e7f3] rounded-full hover:bg-[#fafafa] transition-colors"
                            >
                                {search.what}{search.where && `, ${search.where}`} ({search.country.toUpperCase()})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="flex flex-col h-[70vh]">
                    <div className="flex-grow overflow-y-auto pr-4 space-y-4">
                        {isLoading && (
                            <div className="text-center py-8">
                                <svg className="animate-spin h-8 w-8 text-green-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            </div>
                        )}
                        {error ? (
                            <div className="text-center py-8">
                                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6 max-w-md mx-auto">
                                    <div className="text-red-400 text-lg font-semibold mb-2">Error Loading Jobs</div>
                                    <p className="text-red-300 text-sm mb-4">{error}</p>
                                    <button
                                        onClick={() => {
                                            setError(null);
                                            // Retry the last search
                                            const formData = new FormData();
                                            formData.append('country', selectedCountry);
                                            formData.append('page', currentPage);
                                            if (searchQuery.what || searchQuery.where) {
                                                formData.append('what', searchQuery.what);
                                                formData.append('where', searchQuery.where);
                                                fetchJobs(`${API_BASE_URL}/api/jobs/search`, formData);
                                            } else if (resumeData) {
                                                const resumeText = formatResumeDataAsText(resumeData);
                                                formData.append('resume_text', resumeText);
                                                fetchJobs(`${API_BASE_URL}/api/jobs/match`, formData);
                                            } else {
                                                fetchJobs(`${API_BASE_URL}/api/jobs/search`, formData);
                                            }
                                        }}
                                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        ) : jobs && jobs.length > 0 ? (
                            jobs.map(job => (
                                <JobCard
                                    key={job.id}
                                    job={job}
                                    onSelectJob={handleSelectJob}
                                    isSelected={selectedJob?.id === job.id}
                                    isSaved={isSaved(job)}
                                    onToggleSave={() => toggleSave(job)}
                                />
                            ))
                        ) : (
                            !isLoading && <p className="text-center text-gray-500 py-10">No jobs found. Try adjusting your search.</p>
                        )}
                    </div>
                    {jobs.length > 0 && totalPages > 1 && (
                        <div className="pt-4 mt-4 border-t border-gray-700">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
                <div className="h-[70vh] overflow-y-auto bg-[#3C0F6C] rounded-xl border border-gray-700 shadow-sm">
                    {selectedJob ? (
                        <JobDetailView
                            job={selectedJob}
                            isLoading={isDetailLoading}
                            onGenerateCoverLetter={onGenerateCoverLetter}
                            onGenerateInterviewQuestions={onGenerateInterviewQuestions}
                            onFetchMatchDetails={handleFetchMatchDetails}
                            isSaved={isSaved}
                            onToggleSave={toggleSave}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-gray-500">Select a job to see details.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const JobCard = ({ job, onSelectJob, isSelected, isSaved = false, onToggleSave = () => { } }) => {
    const summary = job.summary || "No summary available.";

    const formatSalary = (min, max, currencySymbol = '$') => {
        if (!min && !max) return "Not disclosed";
        const format = (val) => `${currencySymbol}${Math.round(val / 1000)}k`;
        if (min && max) {
            if (min === max) return format(min);
            return `${format(min)} - ${format(max)}`;
        }
        if (min) return `From ${format(min)}`;
        if (max) return `Up to ${format(max)}`;
        return "Not disclosed";
    };

    const currencyMap = {
        'gb': '£', 'us': '$', 'ca': '$', 'au': '$', 'nz': '$', 'sg': '$',
        'de': '€', 'fr': '€', 'it': '€', 'es': '€', 'nl': '€', 'be': '€', 'at': '€',
        'in': '₹', 'br': 'R$', 'mx': '$', 'za': 'R', 'pl': 'zł', 'ch': 'CHF'
    };


    const currencySymbol = currencyMap[job.location.country] || '$';
    const salary = formatSalary(job.salary_min, job.salary_max, currencySymbol);

    return (
        <button
            onClick={() => onSelectJob(job)}
            className={`w-full text-left p-4 rounded-xl border transition-all duration-200 space-y-3 relative ${isSelected ? 'bg-gray-800 border-green-500 shadow-lg' : 'bg-gray-900 border-gray-700 hover:bg-gray-800'}`}
        >
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="text-lg font-bold text-white">{job.title}</h3>
                    <p className="text-gray-300 font-medium">{job.company.display_name}</p>
                </div>
                <div className="flex items-center gap-2">
                    {job.category && job.category.label && (
                        <span className="flex-shrink-0 bg-green-500/10 text-green-300 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 text-center">
                            <Tag size={12} />
                            {job.category.label}
                        </span>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
                        className="ml-2 p-1.5 rounded-md hover:bg-gray-800 border border-gray-700"
                        aria-label={isSaved ? 'Unsave job' : 'Save job'}
                        title={isSaved ? 'Unsave' : 'Save'}
                    >
                        {isSaved ? <BookmarkCheck size={18} className="text-yellow-400" /> : <Bookmark size={18} className="text-yellow-400" />}
                    </button>
                </div>
            </div>

            <div className="text-sm text-gray-400 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5"><MapPin size={14} /> {job.location.display_name}</span>
                <span className="flex items-center gap-1.5"><Briefcase size={14} /> {job.contract_time || 'Full-time'}</span>
                <span className="flex items-center gap-1.5"><DollarSign size={14} /> {salary}</span>
            </div>

            <p className="text-sm text-gray-500 pt-2 border-t border-gray-700/50">{summary}</p>
        </button>
    );
};


const JobDetailView = ({ job, isLoading, onGenerateCoverLetter, onGenerateInterviewQuestions, onFetchMatchDetails, isSaved, onToggleSave }) => {
    const { matchDetails } = job;
    const [isFetchingFullJD, setIsFetchingFullJD] = useState(false);



    const handleGenerateCoverLetterClick = async () => {
        setIsFetchingFullJD(true);
        try {
            const { data } = await apiCall('/api/jobs/get-full-description', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(job),
            });
            onGenerateCoverLetter(data.full_description);
        } catch (error) {
            console.error("Error fetching full job description:", error);
            onGenerateCoverLetter(job.description);
        } finally {
            setIsFetchingFullJD(false);
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="text-2xl font-bold text-white">{job.title}</h3>
                    <p className="text-lg text-gray-300 font-medium">{job.company.display_name}</p>
                    <p className="text-sm text-gray-400 mt-1">{job.location.display_name}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onToggleSave(job)}
                        className="p-2 rounded-md hover:bg-gray-800 border border-gray-700"
                        aria-label={isSaved(job) ? 'Unsave job' : 'Save job'}
                        title={isSaved(job) ? 'Unsave' : 'Save'}
                    >
                        {isSaved(job) ? <BookmarkCheck size={18} className="text-yellow-400" /> : <Bookmark size={18} className="text-yellow-400" />}
                    </button>
                    <a href={job.redirect_url} target="_blank" rel="noopener noreferrer" className="bg-green-500 text-gray-900 font-semibold py-2 px-4 rounded-lg hover:bg-green-600 transition-colors flex-shrink-0">
                        Apply
                    </a>
                </div>
            </div>
            <div className="mt-6">
                <h4 className="text-lg font-bold mb-2 text-white">Prepare for this job</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {!matchDetails ? (
                        <button onClick={() => onFetchMatchDetails(job)} disabled={isLoading} className="p-4 bg-gray-800 rounded-lg text-center hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-wait">
                            <BarChart2 size={24} className="mx-auto text-green-400 mb-1" />
                            <p className="text-sm font-semibold text-white">{isLoading ? 'Analyzing...' : 'View Match Score'}</p>
                        </button>
                    ) : (
                        <div className="p-4 bg-gray-800 rounded-lg text-center">
                            <p className="text-gray-400 text-sm font-semibold">Match Score</p>
                            {matchDetails.error ? (
                                <p className="text-2xl font-bold text-red-500">Error</p>
                            ) : (
                                <p className={`text-2xl font-bold ${matchDetails.match_score > 80 ? 'text-green-400' : 'text-yellow-400'}`}>{matchDetails.match_score}%</p>
                            )}
                        </div>
                    )}

                    <button
                        onClick={handleGenerateCoverLetterClick}
                        disabled={isFetchingFullJD}
                        className="p-4 bg-gray-800 rounded-lg text-center hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-wait"
                    >
                        <FileText size={24} className="mx-auto text-green-400 mb-1" />
                        <p className="text-sm font-semibold text-white">
                            {isFetchingFullJD ? 'Analyzing...' : 'Generate Cover Letter'}
                        </p>
                    </button>

                    <button onClick={() => onGenerateInterviewQuestions(job.description)} className="p-4 bg-gray-800 rounded-lg text-center hover:bg-gray-700 transition-colors">
                        <MessageSquare size={24} className="mx-auto text-green-400 mb-1" />
                        <p className="text-sm font-semibold text-white">Interview Questions</p>
                    </button>
                </div>
            </div>
            <div className="mt-8 pt-6 border-t border-gray-700">
                <h4 className="text-xl font-bold mb-4 text-white">Full Job Description</h4>
                <div className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">
                    {job.description}
                </div>
            </div>
        </div>
    );
};


const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const halfPagesToShow = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow) {
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(i);
        }
    } else {
        if (currentPage <= halfPagesToShow) {
            for (let i = 1; i <= maxPagesToShow - 1; i++) {
                pageNumbers.push(i);
            }
            pageNumbers.push('...');
            pageNumbers.push(totalPages);
        } else if (currentPage >= totalPages - halfPagesToShow) {
            pageNumbers.push(1);
            pageNumbers.push('...');
            for (let i = totalPages - maxPagesToShow + 2; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);
            pageNumbers.push('...');
            for (let i = currentPage - halfPagesToShow + 1; i <= currentPage + halfPagesToShow - 1; i++) {
                pageNumbers.push(i);
            }
            pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
    }

    return (
        <nav className="flex items-center justify-center space-x-2">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ChevronLeft size={20} />
            </button>
            {pageNumbers.map((number, index) =>
                typeof number === 'number' ? (
                    <button
                        key={index}
                        onClick={() => onPageChange(number)}
                        className={`px-4 py-2 rounded-md text-sm font-semibold ${currentPage === number
                            ? 'bg-green-500 text-gray-900'
                            : 'bg-gray-800 hover:bg-gray-700'
                            }`}
                    >
                        {number}
                    </button>
                ) : (
                    <span key={index} className="px-4 py-2 text-gray-500">
                        {number}
                    </span>
                )
            )}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ChevronRight size={20} />
            </button>
        </nav>
    );
};

export default JobMatching;